const DISMISSED_AT_KEY = "sell-it-off:near-me-prompt:dismissed-at";
const DISMISS_COUNT_KEY = "sell-it-off:near-me-prompt:dismiss-count";
const SESSION_SHOWN_KEY = "sell-it-off:near-me-prompt:session-shown";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const SNOOZE_FIRST_DISMISS_DAYS = 7;
const SNOOZE_SECOND_DISMISS_DAYS = 30;
const PASSIVE_ONLY_DISMISS_COUNT = 3;

export type NearMePromptPolicyInput = {
  nearMeOn: boolean;
  dismissedAtIso: string | null;
  dismissCount: number;
  sessionPromptShown: boolean;
  now?: Date;
};

export type NearMePromptPolicyResult = {
  show: boolean;
  passiveOnly: boolean;
  snoozedUntilIso: string | null;
};

function parseIso(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function snoozeDaysForDismissCount(dismissCount: number): number | null {
  if (dismissCount <= 0) return null;
  if (dismissCount === 1) return SNOOZE_FIRST_DISMISS_DAYS;
  if (dismissCount === 2) return SNOOZE_SECOND_DISMISS_DAYS;
  return SNOOZE_SECOND_DISMISS_DAYS;
}

export function computeNearMePromptSnoozeUntil(
  dismissedAtIso: string | null,
  dismissCount: number,
): Date | null {
  const dismissedAt = parseIso(dismissedAtIso);
  if (!dismissedAt || dismissCount <= 0) return null;
  const days = snoozeDaysForDismissCount(dismissCount);
  if (days == null) return null;
  return new Date(dismissedAt.getTime() + days * MS_PER_DAY);
}

/** Whether a proactive near-me discovery modal may open (product consent, not OS push). */
export function shouldShowNearMePrompt(
  input: NearMePromptPolicyInput,
): NearMePromptPolicyResult {
  if (input.nearMeOn) {
    return { show: false, passiveOnly: false, snoozedUntilIso: null };
  }

  if (input.dismissCount >= PASSIVE_ONLY_DISMISS_COUNT) {
    return { show: false, passiveOnly: true, snoozedUntilIso: null };
  }

  const now = input.now ?? new Date();
  const snoozedUntil = computeNearMePromptSnoozeUntil(
    input.dismissedAtIso,
    input.dismissCount,
  );
  if (snoozedUntil && now < snoozedUntil) {
    return {
      show: false,
      passiveOnly: false,
      snoozedUntilIso: snoozedUntil.toISOString(),
    };
  }

  if (input.sessionPromptShown) {
    return { show: false, passiveOnly: false, snoozedUntilIso: null };
  }

  return { show: true, passiveOnly: false, snoozedUntilIso: null };
}

export function readNearMePromptDismissCount(): number {
  if (typeof window === "undefined") return 0;
  const raw = localStorage.getItem(DISMISS_COUNT_KEY);
  const n = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function readNearMePromptDismissedAt(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(DISMISSED_AT_KEY);
}

export function readNearMePromptSessionShown(): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(SESSION_SHOWN_KEY) === "1";
}

export function markNearMePromptSessionShown(): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(SESSION_SHOWN_KEY, "1");
}

export function recordNearMePromptDismissed(nowIso?: string): {
  dismissCount: number;
  dismissedAtIso: string;
} {
  const dismissedAtIso = nowIso ?? new Date().toISOString();
  if (typeof window === "undefined") {
    return { dismissCount: 1, dismissedAtIso };
  }
  const nextCount = readNearMePromptDismissCount() + 1;
  localStorage.setItem(DISMISS_COUNT_KEY, String(nextCount));
  localStorage.setItem(DISMISSED_AT_KEY, dismissedAtIso);
  return { dismissCount: nextCount, dismissedAtIso };
}

export function clearNearMePromptDismissState(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(DISMISS_COUNT_KEY);
  localStorage.removeItem(DISMISSED_AT_KEY);
}
